import styled from '@emotion/styled';
import React, { useCallback, useMemo } from 'react';
import { OnMutateFn } from '@diritto/common/base/mutateFunction';
import { ReplyPublicRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ReplyApi } from 'api/ApiHelper';
import { ModalType } from 'constants/modal';
import { ReplyConfigText, ReplyEditAction } from 'constants/reply';
import { useAuthState } from 'contexts/AuthProvider';
import { useEpisodeReplyDispatch } from 'contexts/EpisodeReplyProvider';
import { MoreMenu } from 'ui-components/common/MoreMenu';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { showAlertModal, showToast } from 'utils/modal';
import { ModalHandler } from 'utils/ModalHandler';

interface ReplyMoreMenuProps {
  reply: ReplyPublicRelayDto;
  actions: ReplyEditAction[];
  hasReReply?: boolean;
  onDispatchAction: (
    action: ReplyEditAction,
    reply: ReplyPublicRelayDto,
    onMutateFn?: OnMutateFn,
    options?: any,
  ) => void;
}

export const ReplyMoreMenu = ({ onDispatchAction, reply, actions, hasReReply = false }: ReplyMoreMenuProps) => {
  const { isLoggedIn, user } = useAuthState();
  const requestSign = useRequestSign();
  const requestCertificate = useRequestCertificate();

  const ctxString = reply.parentId ? '답글' : '댓글';
  const { onSettled } = useEpisodeReplyDispatch();

  const handleReport = useCallback(async () => {
    if (!isLoggedIn) {
      requestSign();
      return;
    }

    try {
      const data = await ReplyApi.isReported(reply.id);
      if (data.isReported) {
        showToast(`이미 신고한 ${ctxString}이에요.`);
        return;
      }
    } catch {
      showToast('일시적인 오류로 신고하지 못했어요. 잠시 뒤 다시 시도해주세요.');
      return;
    }

    ModalHandler.show(ModalType.ReplyReport, {
      replyId: reply.id,
      ctxString,
      onSettled,
    });
  }, [ctxString, onSettled, isLoggedIn, reply, requestSign]);

  const handleDelete = useCallback(
    (action: ReplyEditAction) => {
      showAlertModal(
        {
          title: `${ctxString}을 삭제하시겠어요?`,
          description: hasReReply
            ? '삭제는 6시간 뒤에 이루어지며, 되돌릴 수 없어요.\n댓글에 담겨진 답글은 삭제되지 않아요.'
            : '삭제는 6시간 뒤에 이루어지며, 되돌릴 수 없어요.',
        },
        {
          onConfirm: () => onDispatchAction?.(action, reply),
          confirmText: '삭제',
          cancelText: '취소',
        },
      );
    },
    [ctxString, hasReReply, onDispatchAction, reply],
  );

  const handleDeleteByAuthor = useCallback(
    (action: ReplyEditAction) => {
      showAlertModal(
        {
          title: `${ctxString}을 삭제하시겠어요?`,
          description: hasReReply ? '댓글에 담겨진 답글은 삭제되지 않아요.' : '',
        },
        {
          onConfirm: () => onDispatchAction?.(action, reply),
          confirmText: '삭제',
          cancelText: '취소',
        },
      );
    },
    [ctxString, hasReReply, onDispatchAction, reply],
  );

  const handleAction = useCallback(
    async (action: ReplyEditAction) => {
      if (!user?.ageVerified) {
        showAlertModal(
          { title: '본인인증을 해주세요.', description: '해당 기능을 사용하려면 본인인증이 필요해요.' },
          {
            onConfirm: requestCertificate,
            confirmText: '본인인증하러 가기',
            cancelText: '닫기',
          },
        );
        return;
      }

      switch (action) {
        case ReplyEditAction.ActiveEdit:
          onDispatchAction?.(action, reply);
          return;
        case ReplyEditAction.DeleteByUser:
          handleDelete(action);
          return;
        case ReplyEditAction.DeleteByAuthor:
          handleDeleteByAuthor(action);
          return;
        case ReplyEditAction.Report:
          await handleReport();
          return;
        case ReplyEditAction.Pin:
          onDispatchAction?.(action, reply, {
            onSuccess: () => {
              showToast('댓글이 고정되었어요.');
            },
            onError: () => {
              showToast('일시적인 오류로 댓글을 고정하지 못했어요. 잠시 뒤 다시 시도해주세요.');
            },
          });
          return;
        case ReplyEditAction.Unpin:
          onDispatchAction?.(action, reply, {
            onSuccess: () => {
              showToast('댓글이 고정이 해제되었어요.');
            },
            onError: () => {
              showToast('일시적인 오류로 해제하지 못했어요. 잠시 뒤 다시 시도해주세요.');
            },
          });
          return;
        default:
          console.log("Can't find action");
      }
    },
    [user?.ageVerified, requestCertificate, onDispatchAction, reply, handleDelete, handleDeleteByAuthor, handleReport],
  );

  const menu = useMemo(() => {
    const TextMap = ReplyConfigText(ctxString);
    return actions.map(action => ({
      title: TextMap[action] || '',
      onClick: () => handleAction(action),
      disabled: action === ReplyEditAction.DeleteByUser && !!reply.scheduledDeletedAt,
    }));
  }, [actions, ctxString, handleAction, reply.scheduledDeletedAt]);

  return <StyledMoreMenu vertical menu={menu} />;
};

const StyledMoreMenu = styled(MoreMenu)`
  margin-left: auto;
`;
